<template>
    <div class="w-full grid">
        <GenericChartModal v-if="expandable" :chart-module="chartModule" :options="options" :chart-state="chartState" :print-subtitle="printSubtitle" @close="onModalClose">
            <template #default="scope">
                <div class="relative grid" :style="{ minHeight: (options as any)?.chart?.height ? `${(options as any).chart.height}px` : 'auto' }">
                    <div
                        ref="chartElement"
                        class="w-full"
                        :class="{
                            'highcharts-dashboards-container': isDashboard,
                        }"
                    ></div>
                    <div class="absolute top-0 right-2 flex items-center gap-1.5 z-10">
                        <slot name="prepend-actions" />
                        <button class="flex items-center justify-center p-1 -mr-0.5 rounded-full transition-colors hover:bg-gray-100" @click="openModal(scope.open)">
                            <Icon class="h-6 w-auto -mt-0.5" :src="IconSource.SearchPlus"></Icon>
                        </button>
                        <slot name="append-actions" />
                    </div>
                </div>
            </template>
        </GenericChartModal>
        <div v-else ref="chartElement" class="w-full" :class="{ 'highcharts-dashboards-container': isDashboard }"></div>
    </div>
</template>

<script lang="ts">
import { defineComponent, watch, ComputedRef, computed, isRef, ref, onMounted, onBeforeUnmount, unref, nextTick } from 'vue';
import HighchartsMore from 'highcharts/highcharts-more';
import * as Highcharts from 'highcharts';
import isEqual from 'lodash-es/isEqual';
import { Chart } from 'highcharts';
import merge from 'lodash-es/merge';
import { createChart, type ChartOptions } from '@/components/charts/createChart';
import GenericChartModal from '@/components/charts/GenericChartModal.vue';
import { ChartType, IconSource } from '@/types';

export default defineComponent({
    components: { GenericChartModal },
    props: {
        options: {
            type: Object as () => ComputedRef<ChartOptions> | ChartOptions,
            default: null,
        },
        chartModule: {
            type: Function,
            default: null,
        },
        expandable: {
            type: Boolean,
            default: true,
        },
        printSubtitle: {
            type: String as () => null | string,
            default: null,
        },
        destroyOnUpdate: {
            type: Boolean as () => boolean,
            default: false,
        },
        hasOptions: {
            type: Boolean as () => boolean,
            default: false,
        },
    },
    emits: ['ready', 'update'],
    setup(props, { emit }) {
        const chartElement = ref<null | HTMLElement>(null);

        HighchartsMore(Highcharts);
        if (props.chartModule) {
            props.chartModule(Highcharts);
        }

        const highcharts = ref<Chart | any>();

        // Only a computed handed in whole carries the chart type on .value; a plain options object never matched.
        const isDashboard = computed(() => isRef(props.options) && props.options.value?.type === ChartType.Dashboard);

        let currentActiveOptions = null as null | Highcharts.Options;

        const chartState = ref<{
            options: Highcharts.Options;
            seriesVisibility: boolean[];
            navigator: {
                min: number | undefined;
                max: number | undefined;
            };
        }>();

        async function openModal(open: CallableFunction) {
            const chartInstance = highcharts.value as Chart;
            const series = chartInstance.series as Highcharts.Series[];

            // Read each series' own live `.options`, not `chartInstance.options.series`: the latter
            // reflects only what the chart was first constructed with, and a chart whose series
            // arrive after an async fetch (added via `chart.update()`/`addSeries()` once the data
            // resolves, rather than present at construction) never gets that array backfilled -
            // `chartInstance.series` still has every series, `chartInstance.options.series` can stay
            // stuck at `[]`. Harmless for a chart with one series that IS ready at construction
            // (every other Records chart); silently opened an empty modal for one that starts empty
            // and fills in later (Deliveries's three-way async fetch).
            //
            // A chart with a navigator (`navigator.enabled: true`) also carries a series Highcharts
            // generated for itself - `isInternal: true`, bound to the navigator's own internal axis
            // ids rather than any axis this chart's options declare. Re-handing that series to a
            // fresh chart build asks it to bind to an axis that doesn't exist yet (the navigator
            // hasn't been (re)created at that point), which throws Highcharts error #18 and leaves
            // the modal with no chart at all - so it's excluded here and left for the navigator to
            // regenerate on its own in the modal's fresh build, same as it did in this one.
            const mergedOptions = merge({}, chartInstance.options, {
                series: series
                    .filter((s) => !(s.options as { isInternal?: boolean }).isInternal)
                    .map((s) => ({
                        ...s.options,
                        id: `${s.options.id ?? s.index}-generic-chart-modal`,
                    })),
            });

            const currentState = {
                options: mergedOptions,
                seriesVisibility: series.map((s) => s.visible),
                navigator: {
                    min: chartInstance?.xAxis?.[0]?.min ?? undefined,
                    max: chartInstance?.xAxis?.[0]?.max ?? undefined,
                },
            };

            chartState.value = currentState;

            await nextTick();

            open();
        }

        function onModalClose() {
            chartState.value = undefined;
        }

        onMounted(async () => {
            if (chartElement.value) {
                const unreffedOptions = unref(props.options);
                currentActiveOptions = unreffedOptions;

                highcharts.value = createChart(chartElement.value, unreffedOptions);

                await nextTick();

                emit('ready', highcharts.value);
            }
        });

        onBeforeUnmount(() => {
            if (highcharts.value && highcharts.value.destroy) {
                highcharts.value.destroy();
            }
        });

        watch(
            () => props.options,
            async (newValue: ComputedRef<ChartOptions> | ChartOptions) => {
                const unreffedOptions = unref(newValue);
                if (highcharts.value && currentActiveOptions && !isEqual(unreffedOptions, currentActiveOptions)) {
                    currentActiveOptions = unreffedOptions;

                    if (props.destroyOnUpdate && highcharts.value?.destroy && chartElement.value) {
                        highcharts.value.destroy();

                        highcharts.value = createChart(chartElement.value, unreffedOptions);
                    } else {
                        highcharts.value.update(unreffedOptions, true, true, true);
                    }

                    await nextTick();

                    emit('update', highcharts.value);
                }
            },
            { deep: true }
        );

        return { chartElement, isDashboard, highcharts, onMounted, onBeforeUnmount, ChartType, openModal, chartState, IconSource, onModalClose };
    },
});
</script>
